/** OTIS row identity: id + name for DC, org, and account (aligned with backend). */

export const OTIS_KEY_SEP = "\x1f";

export function cleanOtisString(val: unknown): string {
  if (val == null) return "";
  return String(val).trim();
}

export function normalizeNullableId(val: unknown): string | null {
  const s = cleanOtisString(val);
  return s === "" ? null : s;
}

export function resolveDistributionChannelName(
  channelId: unknown,
  channelName: unknown
): string | null {
  const name = cleanOtisString(channelName);
  if (name) return name;
  return normalizeNullableId(channelId);
}

export function resolveOrgName(_orgId: unknown, orgName: unknown): string | null {
  const name = cleanOtisString(orgName);
  return name === "" ? null : name;
}

export function resolveAccountName(
  _accountNumber: unknown,
  accountName: unknown
): string | null {
  const name = cleanOtisString(accountName);
  return name === "" ? null : name;
}

export function buildOtisOrgKey(
  orgId: string | null | undefined,
  orgName: string | null | undefined
): string {
  return `${normalizeNullableId(orgId) ?? ""}${OTIS_KEY_SEP}${
    resolveOrgName(orgId, orgName) ?? ""
  }`;
}

export function buildOtisAccountKey(
  accountNumber: string | null | undefined,
  accountName: string | null | undefined
): string {
  return `${normalizeNullableId(accountNumber) ?? ""}${OTIS_KEY_SEP}${
    resolveAccountName(accountNumber, accountName) ?? ""
  }`;
}

export function buildOtisFormFieldKey(params: {
  recordId: string;
  orgId?: string | null;
  orgName?: string | null;
  accountId?: string | null;
  accountName?: string | null;
  field: string;
  isMainRow?: boolean;
  isOrgLevelRow?: boolean;
}): string {
  const {
    recordId,
    orgId,
    orgName,
    accountId,
    accountName,
    field,
    isMainRow,
    isOrgLevelRow,
  } = params;

  if (isMainRow || (!orgId && !orgName && !accountId && !accountName)) {
    return `${recordId}---${field}`;
  }

  const orgKey = buildOtisOrgKey(orgId, orgName);
  if (isOrgLevelRow || !accountId) {
    return `${recordId}-${orgKey}--${field}`;
  }

  const accKey = buildOtisAccountKey(accountId, accountName);
  return `${recordId}-${orgKey}-${accKey}-${field}`;
}

const OTIS_NUMERIC_FIELDS = [
  "pub_goal",
  "currEst",
  "suppEst",
  "compInit",
  "compGross",
  "compNet",
  "retPercentage",
  "cpos4wkPOS",
  "cpos8wkPOS",
  "Cpos_LTD",
  "sales_notes",
  "status",
] as const;

/** Extract trailing field name from a form key (main / org / account layouts). */
export function extractOtisFieldFromFormKey(key: string): string | null {
  if (key.includes("---")) {
    const field = key.slice(key.lastIndexOf("---") + 3);
    return field || null;
  }
  if (key.includes("--")) {
    const field = key.slice(key.lastIndexOf("--") + 2);
    return field || null;
  }
  for (const field of OTIS_NUMERIC_FIELDS) {
    if (key.endsWith(`-${field}`)) return field;
  }
  return null;
}

export function matchesDistributionChannel(a: any, b: any): boolean {
  const aId = normalizeNullableId(
    a?.DISTRIBUTION_CHANNEL ?? a?.distribution_channel
  );
  const bId = normalizeNullableId(
    b?.DISTRIBUTION_CHANNEL ?? b?.distribution_channel
  );
  const aName = resolveDistributionChannelName(
    aId,
    a?.DISTRIBUTION_CHANNEL_NAME ?? a?.distribution_channel_name
  );
  const bName = resolveDistributionChannelName(
    bId,
    b?.DISTRIBUTION_CHANNEL_NAME ?? b?.distribution_channel_name
  );
  return aId === bId && aName === bName;
}

export function matchesOrganization(a: any, b: any): boolean {
  const aId = normalizeNullableId(a?.organizationId ?? a?.ORGANIZATION_ID);
  const bId = normalizeNullableId(b?.organizationId ?? b?.ORGANIZATION_ID);
  const aName = resolveOrgName(aId, a?.orgName ?? a?.ORG_NAME);
  const bName = resolveOrgName(bId, b?.orgName ?? b?.ORG_NAME);
  return aId === bId && aName === bName;
}

export function matchesAccount(a: any, b: any): boolean {
  const aNum = normalizeNullableId(a?.accountNumber ?? a?.ACCOUNT_NBR);
  const bNum = normalizeNullableId(b?.accountNumber ?? b?.ACCOUNT_NBR);
  const aName = resolveAccountName(aNum, a?.accountName ?? a?.ACCOUNT_NAME);
  const bName = resolveAccountName(bNum, b?.accountName ?? b?.ACCOUNT_NAME);
  return aNum === bNum && aName === bName;
}

export function findOrganization(
  organizations: any[] | undefined,
  orgId: string | null | undefined,
  orgName?: string | null
): any | undefined {
  const probe = { organizationId: orgId ?? null, orgName: orgName ?? null };
  return (organizations || []).find((org) => matchesOrganization(org, probe));
}

export function findAccount(
  accounts: any[] | undefined,
  accountId: string | null | undefined,
  accountName?: string | null
): any | undefined {
  const probe = {
    accountNumber: accountId ?? null,
    accountName: accountName ?? null,
  };
  return (accounts || []).find((acc) => matchesAccount(acc, probe));
}

export function buildOtisOrgExpansionKey(org: {
  organizationId?: string | null;
  orgName?: string | null;
}): string {
  return buildOtisOrgKey(org?.organizationId, org?.orgName);
}
